import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import { classifyMediaParam, isMediaName, MEDIA_KIND_META } from '../../createScreen/utils/parameterUtils';

const PRIMARY = '#FF7E67';

// Format parameter name to readable label
const formatParamLabel = (name, title) => {
  const base = title || name;
  return base
    .replace(/[0-9]+$/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase())
    .replace(/Url/g, 'URL')
    .replace(/Urls/g, 'URLs')
    .trim();
};

// Parse condition_rule JSON
const parseConditionRule = (rule) => {
  if (!rule) return null;
  if (typeof rule === 'object') return rule;
  try { return JSON.parse(rule); } catch { return null; }
};

// Check if value is set
const hasValue = (value) => {
  if (value === null || value === undefined || value === '' || value === 'null') return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
};

export default function SettingsModal({
  open,
  onClose,
  settings,
  setSettings,
  selectedModel,
  textInputs,
  setTextInputs,
  uploadedImages,
  setUploadedImages
}) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showAll, setShowAll] = useState(false);

  // Handle open/close animation with proper timing
  useEffect(() => {
    if (open) {
      setIsVisible(true);
      const timer = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => setIsVisible(false), 250);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Categorize all parameters
  const paramCategories = useMemo(() => {
    if (!selectedModel?.parameters) return { text: [], select: [], image: [], settings: [], all: [] };
    
    const text = [];
    const select = [];
    const image = [];
    const settingsParams = [];
    
    selectedModel.parameters.forEach(p => {
      if (p.is_private) return; // Skip private params
      
      const name = p.parameter_name.toLowerCase();

      // Media params (image / video / audio). This used to have no `url` token,
      // so object[] params like reference_video_urls matched nothing here and
      // fell through into the generic "settings" bucket — different from what
      // ZenControlPanel did with the same param. Shared classifier now.
      if (classifyMediaParam(p)) {
        image.push(p);
        return;
      }

      // Select params (strings with allowed_values)
      if (p.data_type === 'string' && p.allowed_values && !isMediaName(name) && !name.includes('webhook')) {
        select.push(p);
        return;
      }

      // Text params (strings without allowed_values, not media/webhooks)
      if (p.data_type === 'string' && !p.allowed_values && !isMediaName(name) && !name.includes('webhook')) {
        text.push(p);
        return;
      }
      
      // Everything else goes to settings
      settingsParams.push(p);
    });
    
    return { 
      text, 
      select, 
      image, 
      settings: settingsParams, 
      all: selectedModel.parameters.filter(p => !p.is_private)
    };
  }, [selectedModel]);

  // Params shown in main UI (text + select + image)
  const mainUIParams = useMemo(() => {
    return new Set([
      ...paramCategories.text.map(p => p.parameter_name),
      ...paramCategories.select.map(p => p.parameter_name),
      ...paramCategories.image.map(p => p.parameter_name),
    ]);
  }, [paramCategories]);

  // Params to display based on showAll toggle
  const displayParams = useMemo(() => {
    if (showAll) {
      return paramCategories.all;
    }
    // Only show settings params (not in main UI)
    return paramCategories.settings;
  }, [showAll, paramCategories]);

  // Compute disabled and restricted params based on condition_rule (mirrors ControlBar)
  const { disabledParams, restrictedValues } = useMemo(() => {
    if (!selectedModel?.parameters) return { disabledParams: {}, restrictedValues: {} };

    const disabled = {};
    const restricted = {};

    selectedModel.parameters.forEach(param => {
      const rule = parseConditionRule(param.condition_rule);
      if (!rule) return;

      const paramName = param.parameter_name;
      const paramValue = textInputs?.[paramName] || uploadedImages?.[paramName];
      const paramHasValue = hasValue(paramValue);

      if (rule.restricts_when_present && paramHasValue) {
        Object.entries(rule.restricts_when_present).forEach(([targetParam, restriction]) => {
          let allowedValues = restriction.allowed_values;
          if (typeof allowedValues === 'string') {
            try { allowedValues = JSON.parse(allowedValues); }
            catch { allowedValues = allowedValues.split(',').map(v => v.trim()); }
          }
          restricted[targetParam] = {
            allowedValues: allowedValues.map(String),
            reason: restriction.reason || `Must be ${allowedValues.join(' or ')} when using ${param.parameter_title || paramName}`,
            sourceParam: paramName
          };
        });
      }

      if (rule.requires_min_count) {
        for (const [depParam, minCount] of Object.entries(rule.requires_min_count)) {
          const depValue = textInputs?.[depParam] || uploadedImages?.[depParam];
          const actualCount = Array.isArray(depValue) ? depValue.length : 0;
          if (actualCount < Number(minCount)) {
            disabled[paramName] = {
              reason: rule.reason || `Requires at least ${minCount} ${depParam.replace(/_/g, ' ')}`,
              conflictsWith: depParam
            };
            break;
          }
        }
      }
    });

    return { disabledParams: disabled, restrictedValues: restricted };
  }, [selectedModel, textInputs, uploadedImages]);

  // Auto-correct values when restrictions apply
  useEffect(() => {
    if (Object.keys(restrictedValues).length === 0) return;
    
    Object.entries(restrictedValues).forEach(([paramName, restriction]) => {
      // Check both textInputs and settings
      const currentValue = textInputs?.[paramName] ?? settings?.[paramName];
      if (!currentValue) return;
      
      const isAllowed = restriction.allowedValues.includes(String(currentValue));
      if (!isAllowed && restriction.allowedValues.length > 0) {
        // Always correct into textInputs — that is the only map buildModelParams
        // reads model params from. Writing to `settings` left the illegal value
        // on the request.
        setTextInputs(prev => ({ ...prev, [paramName]: restriction.allowedValues[0] }));
      }
    });
  }, [restrictedValues]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isVisible) return null;

  const inputStyle = {
    width: '100%',
    height: 40,
    padding: '0 12px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8,
    color: '#fff',
    fontSize: 14,
    outline: 'none'
  };

  // Render a parameter input based on its type
  const renderParamInput = (param, isInMainUI = false) => {
    // Determine which state to use based on param type
    const isTextParam = paramCategories.text.some(p => p.parameter_name === param.parameter_name);
    const isSelectParam = paramCategories.select.some(p => p.parameter_name === param.parameter_name);
    const isImageParam = paramCategories.image.some(p => p.parameter_name === param.parameter_name);
    
    // Get value and onChange based on param category
    let value, onChange;
    
    if (isTextParam || isSelectParam) {
      // Text and select params use textInputs
      value = textInputs?.[param.parameter_name] ?? param.default_value ?? '';
      onChange = (v) => setTextInputs?.(prev => ({ ...prev, [param.parameter_name]: v }));
    } else if (isImageParam) {
      // Image params use uploadedImages
      value = uploadedImages?.[param.parameter_name] ?? '';
      onChange = (v) => setUploadedImages?.(prev => ({ ...prev, [param.parameter_name]: v }));
    } else {
      // Everything else (numeric / boolean model params) lives in textInputs —
      // that is what buildModelParams and the panel's Advanced section read.
      // Writing these into `settings` made every edit here a silent no-op,
      // because buildModelParams only reads ratio/quality/width/height off it.
      // `settings` is kept as a read fallback for values migrated from the
      // old shape so an in-flight session doesn't lose what it had.
      value = textInputs?.[param.parameter_name]
        ?? settings[param.parameter_name]
        ?? param.default_value
        ?? '';
      onChange = (v) => setTextInputs?.(prev => ({ ...prev, [param.parameter_name]: v }));
    }
    
    // Handle media params (image / video / audio)
    if (isImageParam) {
      const currentImages = Array.isArray(value) ? value : (value ? [value] : []);
      const mediaKind = classifyMediaParam(param)?.kind || 'image';
      const mediaLabel = MEDIA_KIND_META[mediaKind].label;
      return (
        <div>
          {currentImages.length > 0 && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              {currentImages.map((img, idx) => (
                <div key={idx} style={{ position: 'relative' }}>
                  {mediaKind === 'image' && (
                    <img src={img} alt="" style={{ width: 60, height: 60, borderRadius: 6, objectFit: 'cover' }} />
                  )}
                  {mediaKind === 'video' && (
                    <video src={img} muted playsInline preload="metadata" style={{ width: 60, height: 60, borderRadius: 6, objectFit: 'cover', background: '#000', display: 'block' }} />
                  )}
                  {mediaKind === 'audio' && (
                    <div style={{ width: 60, height: 60, borderRadius: 6, display: 'grid', placeItems: 'center', background: 'rgba(255,255,255,.06)', fontSize: 20 }} title={img}>♪</div>
                  )}
                  <button
                    onClick={() => {
                      if (Array.isArray(value)) {
                        const newArr = [...value];
                        newArr.splice(idx, 1);
                        onChange(newArr.length > 0 ? newArr : null);
                      } else {
                        onChange(null);
                      }
                    }}
                    style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', background: '#ef4444', border: 'none', color: '#fff', fontSize: 10, cursor: 'pointer' }}
                  ><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
                </div>
              ))}
            </div>
          )}
          <div style={{ fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>
            Use the upload button in the input bar to add {mediaLabel}
          </div>
        </div>
      );
    }

    // Boolean
    if (param.data_type === 'boolean') {
      const isDisabled = !!disabledParams[param.parameter_name];
      return (
        <div>
          <button
            onClick={() => !isDisabled && onChange(!value)}
            title={isDisabled ? disabledParams[param.parameter_name]?.reason : undefined}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              background: isDisabled ? 'rgba(255,255,255,0.03)' : value ? PRIMARY : 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: isDisabled ? '#4b5563' : value ? '#fff' : '#9ca3af',
              fontSize: 13,
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              opacity: isDisabled ? 0.5 : 1,
            }}
          >
            {value ? 'Enabled' : 'Disabled'}
          </button>
          {isDisabled && (
            <div style={{ marginTop: 4, fontSize: 11, color: '#6b7280' }}>
              {disabledParams[param.parameter_name]?.reason}
            </div>
          )}
        </div>
      );
    }

    // Dropdown with allowed values
    if (param.allowed_values) {
      let options = Array.isArray(param.allowed_values) 
        ? param.allowed_values.map(String)
        : (() => {
            try {
              const parsed = JSON.parse(param.allowed_values);
              return Array.isArray(parsed) ? parsed.map(String) : [String(parsed)];
            } catch {
              return String(param.allowed_values).split(',').map(s => s.trim());
            }
          })();
      
      // Check for restrictions on this param
      const restriction = restrictedValues[param.parameter_name];
      const hasRestriction = !!restriction;
      
      return (
        <div>
          {hasRestriction && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 6, 
              marginBottom: 8,
              padding: '8px 10px',
              background: 'rgba(251, 191, 36, 0.1)',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              borderRadius: 6
            }}>
              <AlertTriangle size={14} style={{ color: '#fbbf24' }} />
              <span style={{ fontSize: 12, color: '#fbbf24' }}>
                {restriction.reason}
              </span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* "None" option for optional params */}
            {(Number(param.is_required) !== 1 && param.is_required !== true) && (
              <button
                onClick={() => onChange(null)}
                style={{
                  padding: '8px 16px',
                  borderRadius: 8,
                  background: (value === null || value === undefined || value === '') ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.03)',
                  border: '1px dashed rgba(255,255,255,0.2)',
                  color: '#6b7280',
                  fontSize: 13,
                  fontStyle: 'italic',
                  cursor: 'pointer',
                }}
              >
                — None —
              </button>
            )}
            {options.map(opt => {
              const isRestricted = hasRestriction && !restriction.allowedValues.includes(String(opt));
              const isSelected = value !== null && value !== undefined && value !== '' && String(value) === String(opt);
              
              return (
                <button
                  key={opt}
                  onClick={() => !isRestricted && onChange(opt)}
                  disabled={isRestricted}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 8,
                    background: isSelected ? PRIMARY : 'rgba(255,255,255,0.05)',
                    border: isRestricted ? '1px dashed rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.1)',
                    color: isRestricted ? '#4b5563' : isSelected ? '#fff' : '#d1d5db',
                    fontSize: 13,
                    fontWeight: isSelected ? 600 : 400,
                    cursor: isRestricted ? 'not-allowed' : 'pointer',
                    opacity: isRestricted ? 0.5 : 1,
                    textDecoration: isRestricted ? 'line-through' : 'none',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {String(opt).replace(/_/g, ' ')}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    // Number with range
    if (param.data_type === 'number' || param.data_type === 'integer' || param.data_type === 'float') {
      // Try different property names for min/max
      const min = param.min_value ?? param.allowed_min_value ?? param.minimum ?? 0;
      const max = param.max_value ?? param.allowed_max_value ?? param.maximum ?? 10000;
      const step = param.data_type === 'float' ? 0.1 : 1;
      
      // For large ranges (>500), just show number input without slider
      const showSlider = (max - min) <= 500;
      
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {showSlider && (
            <input 
              type="range" 
              value={value || min} 
              onChange={(e) => onChange(param.data_type === 'float' ? parseFloat(e.target.value) : parseInt(e.target.value))} 
              min={min} 
              max={max} 
              step={step}
              style={{ flex: 1 }} 
            />
          )}
          <input 
            type="number" 
            value={value || ''} 
            onChange={(e) => {
              const v = param.data_type === 'float' ? parseFloat(e.target.value) : parseInt(e.target.value);
              onChange(isNaN(v) ? '' : v);
            }} 
            min={min} 
            max={max}
            step={step}
            placeholder={`${min} - ${max}`}
            style={{ ...inputStyle, width: showSlider ? 80 : '100%', textAlign: 'center' }} 
          />
        </div>
      );
    }

    // Text area for negative_prompt
    if (param.parameter_name === 'negative_prompt') {
      return (
        <textarea 
          value={value || ''} 
          onChange={(e) => onChange(e.target.value)} 
          placeholder="ugly, blurry, low quality..." 
          rows={3} 
          style={{ ...inputStyle, height: 'auto', padding: '10px 12px', resize: 'none' }} 
        />
      );
    }

    // Default text input
    return (
      <input 
        type="text" 
        value={value || ''} 
        onChange={(e) => onChange(e.target.value)} 
        placeholder={`Enter ${param.parameter_name}`}
        style={inputStyle} 
      />
    );
  };

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ 
          position: 'fixed', 
          inset: 0, 
          background: 'rgba(0,0,0,0.5)', 
          backdropFilter: 'blur(4px)', 
          zIndex: 99998,
          opacity: isAnimating ? 1 : 0,
          transition: 'opacity 0.25s ease'
        }}
      />
      
      {/* Modal Panel */}
      <div style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: 380,
        maxWidth: '100vw',
        background: 'rgba(10,10,18,.98)',
        backdropFilter: 'blur(20px)',
        borderLeft: '1px solid rgba(255,255,255,0.08)',
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        transform: isAnimating ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
        {/* Header */}
        <div style={{ padding: '18px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: '#fff', margin: 0 }}>Advanced Settings</h3>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 6, background: 'rgba(255,255,255,0.05)', border: 'none', color: '#6b7280', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
        </div>

        {/* Show All Toggle */}
        <div style={{ 
          padding: '12px 20px', 
          borderBottom: '1px solid rgba(255,255,255,0.08)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          background: 'rgba(255,255,255,0.02)'
        }}>
          <span style={{ fontSize: 13, color: '#9ca3af' }}>
            Show all parameters
            <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 6 }}>
              ({paramCategories.all.length} total)
            </span>
          </span>
          <button
            onClick={() => setShowAll(!showAll)}
            style={{
              width: 44,
              height: 24,
              borderRadius: 12,
              border: 'none',
              background: showAll ? PRIMARY : 'rgba(255,255,255,0.1)',
              cursor: 'pointer',
              position: 'relative',
              transition: 'background 0.2s'
            }}
          >
            <span style={{
              position: 'absolute',
              top: 2,
              left: showAll ? 22 : 2,
              width: 20,
              height: 20,
              borderRadius: 10,
              background: '#fff',
              transition: 'left 0.2s'
            }} />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {displayParams.length > 0 ? (
            displayParams.map(param => {
              // Check if this param is shown in main UI
              const isInMainUI = mainUIParams.has(param.parameter_name);
              
              return (
                <SettingRow 
                  key={param.parameter_name}
                  label={formatParamLabel(param.parameter_name, param.parameter_title)} 
                  description={param.description || param.help_text}
                  required={Number(param.is_required) === 1 || param.is_required === true}
                  badge={isInMainUI ? 'In Input Bar' : null}
                >
                  {renderParamInput(param, isInMainUI)}
                </SettingRow>
              );
            })
          ) : (
            <div style={{ padding: 20, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
              {selectedModel ? 'No additional settings for this model' : 'Select a model to see settings'}
            </div>
          )}

          {/* Model Info */}
          {selectedModel && (
            <div style={{ padding: 14, background: 'rgba(255,255,255,0.02)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.06)', marginTop: 20 }}>
              <h4 style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8, marginTop: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Model Info</h4>
              <div style={{ fontSize: 13, color: '#9ca3af', lineHeight: 1.8 }}>
                <div><span style={{ color: '#6b7280' }}>Name:</span> {selectedModel.model_name}</div>
                <div><span style={{ color: '#6b7280' }}>Type:</span> {selectedModel.model_type || 'Unknown'}</div>
                <div><span style={{ color: '#6b7280' }}>Cost:</span> ~{selectedModel.average_cost ? Number(selectedModel.average_cost).toFixed(4) : 'N/A'} URO</div>
                {selectedModel.model_eta && <div><span style={{ color: '#6b7280' }}>ETA:</span> ~{selectedModel.model_eta}s</div>}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: 10 }}>
          <button 
            onClick={() => {
              // Reset to model defaults — into textInputs, where params are read
              if (selectedModel?.parameters) {
                const defaults = {};
                selectedModel.parameters.forEach(p => {
                  if (p.is_private) return;
                  if (p.default_value !== undefined && p.default_value !== null) {
                    defaults[p.parameter_name] = p.default_value;
                  }
                });
                setTextInputs(prev => ({ ...prev, ...defaults }));
              }
            }} 
            style={{ flex: 1, height: 38, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: '#9ca3af', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
          >
            Reset
          </button>
          <button onClick={onClose} style={{ flex: 1, height: 38, background: PRIMARY, border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Done</button>
        </div>
      </div>
    </>,
    document.body
  );
}

function SettingRow({ label, description, required, badge, children }) {
  const [showHelp, setShowHelp] = useState(false);
  
  return (
    <div style={{ 
      marginBottom: 20,
      padding: '14px 16px',
      background: 'rgba(255,255,255,0.02)',
      borderRadius: 10,
      border: '1px solid rgba(255,255,255,0.06)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: description ? 6 : 10 }}>
        <label style={{ fontSize: 14, fontWeight: 500, color: '#f5f5f5' }}>
          {label}
          {!!required && <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>}
        </label>
        {description && (
          <button
            onClick={() => setShowHelp(!showHelp)}
            style={{
              background: 'none',
              border: 'none',
              padding: 2,
              cursor: 'pointer',
              color: showHelp ? '#FF7E67' : '#6b7280',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <HelpCircle size={14} />
          </button>
        )}
        {badge && (
          <span style={{
            fontSize: 10,
            padding: '2px 8px',
            borderRadius: 4,
            background: 'rgba(250, 139, 20, 0.15)',
            color: '#FF7E67',
            fontWeight: 500,
            marginLeft: 'auto'
          }}>
            {badge}
          </span>
        )}
      </div>
      {description && showHelp && (
        <div style={{ 
          fontSize: 12, 
          color: '#9ca3af', 
          marginBottom: 10,
          padding: '8px 10px',
          background: 'rgba(255,255,255,0.03)',
          borderRadius: 6,
          lineHeight: 1.5
        }}>
          {description}
        </div>
      )}
      {children}
    </div>
  );
}
